/**
 * Разбор серверных ошибок валидации ASP.NET ValidationProblemDetails (RFC7807):
 * `{ errors: { Field: ["msg1", ...] }, title, status }` — KAN-310.
 * Интерсептор уже нормализует ключи в camelCase, но на всякий случай
 * приводим имена полей сами (учёт PascalCase и префиксов вида "dto.field", "$.field").
 */

export type ServerFieldErrors = Record<string, string[]>;

const toCamelCase = (key: string): string => {
  // "dto.UserName" / "$.userName" → "userName"
  const last = key.split(".").pop() ?? key;
  const clean = last.replace(/^\$/, "");
  if (!clean) return clean;
  return clean.charAt(0).toLowerCase() + clean.slice(1);
};

export function extractServerFieldErrors(data: unknown): ServerFieldErrors | null {
  if (typeof data !== "object" || data === null) return null;
  const errors = (data as Record<string, unknown>)["errors"];
  if (typeof errors !== "object" || errors === null || Array.isArray(errors)) {
    return null;
  }
  const result: ServerFieldErrors = {};
  for (const [field, value] of Object.entries(errors as Record<string, unknown>)) {
    const messages = Array.isArray(value)
      ? value.filter((v): v is string => typeof v === "string" && v.trim().length > 0)
      : typeof value === "string" && value.trim().length > 0
        ? [value]
        : [];
    if (messages.length === 0) continue;
    const key = toCamelCase(field);
    result[key] = [...(result[key] ?? []), ...messages];
  }
  return Object.keys(result).length > 0 ? result : null;
}

/** Короткая сводка для тоста: до 3 полей, по первому сообщению на поле. */
export function summarizeFieldErrors(fieldErrors: ServerFieldErrors): string {
  const entries = Object.entries(fieldErrors);
  const parts = entries.slice(0, 3).map(([, msgs]) => msgs[0]!);
  const more = entries.length - 3;
  return more > 0 ? `${parts.join("; ")} (+${more})` : parts.join("; ");
}

/**
 * Маппит серверные field-ошибки на поля формы через переданный сеттер
 * (совместим с useFormState.setErrors и любым Record-сеттером).
 * Возвращает сообщения, не сопоставившиеся ни с одним полем формы —
 * caller показывает их общим тостом (fallback, не потеря).
 */
export function applyServerFieldErrors(
  fieldErrors: ServerFieldErrors,
  formFields: readonly string[],
  setError: (field: string, message: string) => void,
): string[] {
  const unmatched: string[] = [];
  for (const [field, messages] of Object.entries(fieldErrors)) {
    if (formFields.includes(field)) {
      setError(field, messages[0]!);
    } else {
      unmatched.push(...messages);
    }
  }
  return unmatched;
}

/**
 * Точка подключения для форм (KAN-310): достаёт ValidationProblemDetails прямо
 * из ошибки axios и раскладывает по полям.
 *
 * `handled: true` только если хотя бы одно поле формы совпало — иначе caller
 * показывает обычный тост, чтобы ошибка не потерялась. `unmatched` — сообщения
 * по полям, которых в форме нет (их тоже стоит показать общим тостом).
 */
export function applyServerValidationToForm<TField extends string>(
  error: unknown,
  formFields: readonly TField[],
  setFieldError: (field: TField, message: string) => void,
): { handled: boolean; unmatched: string[] } {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  const fieldErrors = extractServerFieldErrors(data);
  if (!fieldErrors) return { handled: false, unmatched: [] };

  const unmatched = applyServerFieldErrors(fieldErrors, formFields, (field, message) =>
    setFieldError(field as TField, message),
  );
  const matched = Object.keys(fieldErrors).some((field) =>
    (formFields as readonly string[]).includes(field),
  );
  return { handled: matched, unmatched };
}
